import React from 'react';
import { Link, useLocation, useNavigate } from '@remix-run/react';
import LinkButton from "~/ui-apps/design-system/atoms/LinkButton";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faXmark, faListCheck, faCircleQuestion, faDollarSign, faCircleInfo, faPlayCircle } from '@fortawesome/free-solid-svg-icons';
import { MOBILE_HEADER_SCROLL_TRANSITION_CLASS } from "~/ui-apps/design-system/constants/mobileSignedInHeader";
import { useMobileNavScrollVisibility } from "~/ui-apps/hooks/useMobileNavScrollVisibility";
import { isFinancialGlanceTickerPath } from "~/utilities/tickerRoutePaths.util";

interface MobileSignedOutHeaderProps {
    setMobileMenuOpen: (open: boolean) => void;
    mobileMenuOpen: boolean;
}

const MobileSignedOutHeader: React.FC<MobileSignedOutHeaderProps> = ({ setMobileMenuOpen, mobileMenuOpen }) => {
    const location = useLocation();
    const isTickerDetailPath = isFinancialGlanceTickerPath(location.pathname);
    const isHeaderVisible = useMobileNavScrollVisibility();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (!isHeaderVisible && !isTickerDetailPath) {
            setMobileMenuOpen(false);
        }
    }, [isHeaderVisible, isTickerDetailPath, setMobileMenuOpen]);

    // Smooth scroll handler
    const handleSmoothScroll = (
        e: React.MouseEvent<HTMLAnchorElement>,
        targetId: string,
    ) => {
        e.preventDefault();
        setMobileMenuOpen(false);
        const element = document.querySelector(targetId);
        if (element) {
            element.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    };

    const handleLogoNavClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        setMobileMenuOpen(false);
        if (location.pathname === "/") {
            document.getElementById("hero")?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
            if (location.hash || location.search) {
                window.history.replaceState(null, "", "/");
            }
            return;
        }
        navigate("/", { state: { scrollToHero: true } });
    };
    return (
        <header
            id="mobile-header"
            className={
                isTickerDetailPath
                    ? "bg-background-dark"
                    : `block fixed inset-x-0 z-30 bg-background-dark top-0 transition-transform ${MOBILE_HEADER_SCROLL_TRANSITION_CLASS} ${
                        isHeaderVisible ? "translate-y-0" : "-translate-y-full"
                    }`
            }
        >
            <nav aria-label="Global" className="flex items-center justify-between p-4">
                <div className="flex flex-1">
                    <a href="/" onClick={handleLogoNavClick} className="text-inherit no-underline">
                        <span className="tracking-tight text-sm lg:text-base font-bold flex items-center">
                            MarketData
                            <div className="bg-black rounded-md py-[2px] px-[5px] flex items-center justify-center ml-0.5">
                                <span className="text-white text-xs lg:text-base">FYI</span>
                            </div>
                        </span>
                    </a>
                </div>
                <div className="flex">
                    <button
                        type="button"
                        onClick={() => setMobileMenuOpen(true)}
                        className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
                    >
                        <span className="sr-only">Open main menu</span>
                        <FontAwesomeIcon icon={faBars} className="text-xl" />
                    </button>
                </div>
            </nav>

            {/* Overlay */}
            <div
                role="button"
                tabIndex={0}
                className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out z-40
                    ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={() => setMobileMenuOpen(false)}
            />

            {/* Off-canvas menu */}
            <div
                className={`fixed inset-y-0 right-0 z-[60] w-[70%] bg-white transform transition-transform duration-300 ease-in-out
                    ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
                <div className="p-4">
                    <div className="flex items-center justify-end">
                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen(false)}
                            className="-m-2.5 rounded-md p-2.5 text-gray-700"
                        >
                            <span className="sr-only">Close menu</span>
                            <FontAwesomeIcon icon={faXmark} className="h-6 w-6" />
                        </button>
                    </div>
                    <div className="mt-6 flow-root">
                        <div className="-my-6">
                            {location.pathname === "/" ? (
                                <div className="space-y-2 py-2 text-sm font-semibold text-gray-700">
                                    <a
                                        href="#features"
                                        onClick={(e) => handleSmoothScroll(e, "#features")}
                                        className="-mx-3 block rounded-lg px-3 py-2 leading-7"
                                    >
                                        <FontAwesomeIcon icon={faPlayCircle} className="text-lg inline-block mr-2" />
                                        Features
                                    </a>
                                    <div className="border-b border-gray-200"></div>
                                    <a
                                        href="#pricing"
                                        onClick={(e) => handleSmoothScroll(e, "#pricing")}
                                        className="-mx-3 block rounded-lg px-3 py-2 leading-7"
                                    >
                                        <FontAwesomeIcon icon={faDollarSign} className="text-lg inline-block mr-2" />
                                        Pricing
                                    </a>
                                    <div className="border-b border-gray-200"></div>
                                    <a
                                        href="#faq"
                                        onClick={(e) => handleSmoothScroll(e, "#faq")}
                                        className="-mx-3 block rounded-lg px-3 py-2 leading-7"
                                    >
                                        <FontAwesomeIcon icon={faCircleQuestion} className="text-lg inline-block mr-2" />
                                        FAQs
                                    </a>
                                    
                                    <div className="border-b border-gray-200"></div>
                                    <Link
                                        to="/about"
                                        className="-mx-3 block rounded-lg px-3 py-2 leading-7"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <FontAwesomeIcon icon={faCircleInfo} className="text-lg inline-block mr-2" />
                                        About
                                    </Link>
                                    <div className="border-b border-gray-200"></div>
                                </div>
                            ) : (
                                <div className="space-y-2 py-2 text-sm font-semibold text-gray-700">
                                    <Link
                                        to="/#features"
                                        className="-mx-3 block rounded-lg px-3 py-2 leading-7"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <FontAwesomeIcon icon={faPlayCircle} className="text-lg inline-block mr-2" />
                                        Features
                                    </Link>
                                    <div className="border-b border-gray-200"></div>
                                    <Link
                                        to="/#pricing"
                                        className="-mx-3 block rounded-lg px-3 py-2 leading-7"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <FontAwesomeIcon icon={faDollarSign} className="text-lg inline-block mr-2" />
                                        Pricing
                                    </Link>
                                    <div className="border-b border-gray-200"></div>
                                    <Link
                                        to="/#faq"
                                        className="-mx-3 block rounded-lg px-3 py-2 leading-7"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <FontAwesomeIcon icon={faCircleQuestion} className="text-lg inline-block mr-2" />
                                        FAQs
                                    </Link>
                                    <div className="border-b border-gray-200"></div>
                                    <Link
                                        to="/about"
                                        className="-mx-3 block rounded-lg px-3 py-2 leading-7"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <FontAwesomeIcon icon={faCircleInfo} className="text-lg inline-block mr-2" />
                                        About
                                    </Link>
                                    <div className="border-b border-gray-200"></div>
                                </div>
                            )}
                            <div className="py-3 px-10">
                                <LinkButton
                                    href="/sign-in"
                                    type="outline"
                                    size="small"
                                    fullWidth
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Sign In
                                </LinkButton>
                            </div>
                            <div className="py-3 px-10">
                                <LinkButton
                                    href="/sign-up"
                                    type="primary"
                                    size="small"
                                    fullWidth
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Get Started
                                </LinkButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default MobileSignedOutHeader;

